import React, { useMemo } from 'react';
import CustomTextField from '../../../../common/CustomTextField';
import {
  EXCEPTION_CODE,
  MAX_LENGTH_CONSTRAINTS,
  MAX_VALUE_CONSTRAINTS,
  MIN_VALUE
} from '../../../../common/constants';
import Select from '../../../../../../../../common/StyledSelect';
import { IconButton } from '@material-ui/core';
import { DeleteOutlined } from '@material-ui/icons';

const DefaultExceptionForm = ({
  classes,
  index,
  exception,
  onChange,
  packageTypeList,
  exceptionTypeOptions,
  selectedExceptionCodes,
  shipmentPieces,
  isDeliveryException,
  handleRemoveException,
  totalExceptionsCount,
  isMultipleExceptionsEnabled
}) => {
  const warning = useMemo(
    () => exception.code === EXCEPTION_CODE.DELIVERY_SHORT && exception.pieces >= shipmentPieces,
    [exception.code, exception.pieces, shipmentPieces]
  );

  const errorInfo = useMemo(() => exception.errorInfo, [exception.errorInfo]);

  const handleChange = (name) => (value) => {
    if (['code', 'packageType'].includes(name)) {
      value = value && value.value;
    }
    onChange(name)(value);
  };

  const filteredExceptionTypeOptions = useMemo(() => {
    if (!selectedExceptionCodes?.length) {
      return exceptionTypeOptions;
    }

    // Filter out codes selected by other forms, but keep the current form's code
    const selectedCodesSet = new Set(selectedExceptionCodes.filter(Boolean));
    const currentFormCode = exception.code;

    return exceptionTypeOptions
      .filter((option) => option.value === currentFormCode || !selectedCodesSet.has(option.value))
      .map((option) => ({
        ...option,
        label: option.labelText
      }));
  }, [exceptionTypeOptions, selectedExceptionCodes, exception.code]);

  return (
    <div>
      {isMultipleExceptionsEnabled && (
        <div className={classes.exceptionHeaderContainer}>
          <div>Exception {index + 1}</div>
          {totalExceptionsCount > 1 && ( // Only show delete button if there are more than 1 exception
            <IconButton
              aria-label="remove exception"
              title="Remove Exception"
              onClick={() => handleRemoveException(index)}
              classes={{
                root: classes.deleteExceptionButton
              }}>
              <DeleteOutlined />
            </IconButton>
          )}
        </div>
      )}
      {isDeliveryException && (
        <div className={classes.flexRow}>
          <Select
            textFieldProps={{
              id: 'exceptionType',
              label: 'Select Exception',
              variant: 'outlined',
              helperText: errorInfo.code
            }}
            value={exception.code}
            error={Boolean(errorInfo.code)}
            onChange={handleChange('code')}
            data={filteredExceptionTypeOptions}
          />
        </div>
      )}
      <div className={classes.flexRow}>
        <div className={classes.flexRowItem}>
          <CustomTextField
            className={classes.fullWidth}
            id="pieces"
            label="Exception Pieces"
            type="number"
            handleChange={handleChange('pieces')}
            error={errorInfo.pieces}
            value={exception.pieces ?? ''}
            inputProps={{
              min: MIN_VALUE.DELIVERY_PRO_FORM.DE_PIECES,
              max: MAX_VALUE_CONSTRAINTS.DELIVERY_PRO_FORM.DE_PIECES
            }}
          />
        </div>
        <div className={classes.flexRowItem}>
          <Select
            textFieldProps={{
              id: 'packageType',
              label: 'Package Type',
              variant: 'outlined',
              helperText: errorInfo.packageType
            }}
            value={exception.packageType}
            error={Boolean(errorInfo.packageType)}
            onChange={handleChange('packageType')}
            data={packageTypeList}
          />
        </div>
      </div>

      {warning ? (
        <div className={classes.warningText}>
          Exception pieces are equal to or more than billed pieces.
        </div>
      ) : null}

      <CustomTextField
        id="description"
        handleChange={handleChange('description')}
        error={errorInfo.description}
        value={exception.description ?? ''}
        className={classes.fullWidth}
        label="Exception Description"
        inputProps={{
          maxLength: MAX_LENGTH_CONSTRAINTS.DELIVERY_PRO_FORM.DE_COMMENTS,
          focused: true
        }}
      />
    </div>
  );
};

export default DefaultExceptionForm;
